import { AlertTriangle, Ban, Check, Loader2, Minus, X } from 'lucide-react';
import { cn } from '../ui/utils';
import type { JobRecord } from '../../api/jobsApi';
import type { JobStepOutcomes } from '../../contexts/JobCenterContext';
import { PREPARE_STEP_LABELS, stepTone } from './JobCenterDrawer';

/**
 * The live pipeline: one node per prepare step, drawn for every job state.
 *
 * A prepare is the core of this product — the twin, the organ scores, the
 * social context and both summaries all come out of it — and for months its
 * only representation was a spinner and a percentage. This component is the
 * honest picture: which steps are finished, which one is running right now,
 * which are still waiting, and exactly where a failure or cancellation
 * stopped the line.
 *
 * State sources, in order of trust:
 *  - a COMPLETED job's `result.prepared[pid]` per-step statuses (authoritative);
 *  - a RUNNING job's narrated progress feed (JobCenterContext.getSteps),
 *    which the backend emits after each step finishes;
 *  - a terminal job with neither still renders — every node "waiting" —
 *    rather than hiding the pipeline (fail-open to less detail, never to
 *    nothing).
 */

export type PipelineStepState = 'done' | 'warn' | 'failed' | 'active' | 'waiting' | 'stopped';

export interface PipelineStep {
  key: string;
  label: string;
  state: PipelineStepState;
  /** Raw status string for the tooltip (e.g. "skipped_no_change"). */
  detail?: string;
}

const SHORT_LABELS: Record<string, string> = {
  seed: 'Twin',
  organs: 'Organs',
  sdoh: 'Social',
  family: 'Family',
  trajectory: 'Narrative',
  summary: 'Summary',
};

/** Steps in canonical order with legacy-key folding (story -> summary). */
function canonicalSteps(): Array<[string, string]> {
  const seen = new Set<string>();
  const out: Array<[string, string]> = [];
  for (const [key, label] of PREPARE_STEP_LABELS) {
    const canonical = key === 'story' ? 'summary' : key;
    if (seen.has(canonical)) continue;
    seen.add(canonical);
    out.push([canonical, label]);
  }
  return out;
}

/** A completed prepare's per-step statuses for ONE patient, legacy keys folded. */
function resultSteps(job: JobRecord, patientId: string | null): Record<string, string> {
  const prepared = (job.result as Record<string, unknown> | null)?.prepared;
  if (!prepared || typeof prepared !== 'object') return {};
  const entries = Object.entries(prepared as Record<string, unknown>);
  const entry = patientId
    ? entries.find(([pid]) => pid === patientId)
    : entries[0];
  const raw = entry?.[1];
  if (!raw || typeof raw !== 'object') return {};
  const steps: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string' && v) steps[k === 'story' ? 'summary' : k] = v;
  }
  return steps;
}

/**
 * Derive the six pipeline states for a job. Exported for tests — the
 * derivation IS the feature; the rendering is just circles.
 */
export function derivePipeline(
  job: JobRecord,
  live: JobStepOutcomes | undefined,
  patientId: string | null = null,
): PipelineStep[] {
  const fromResult = resultSteps(job, patientId);
  const fromLive: JobStepOutcomes = {};
  for (const [k, v] of Object.entries(live ?? {})) fromLive[k === 'story' ? 'summary' : k] = v;

  const steps = canonicalSteps();
  const out: PipelineStep[] = [];
  let activePlaced = false;

  for (const [key] of steps) {
    const label = SHORT_LABELS[key] ?? key;
    const resultStatus = fromResult[key];
    if (resultStatus !== undefined) {
      const tone = stepTone(resultStatus);
      out.push({
        key,
        label,
        state: tone === 'bad' ? 'failed' : tone === 'warn' ? 'warn' : 'done',
        detail: resultStatus,
      });
      continue;
    }
    const liveStatus = fromLive[key];
    if (liveStatus !== undefined) {
      out.push({ key, label, state: liveStatus === 'failed' ? 'failed' : 'done' });
      continue;
    }
    // No record for this step. What that MEANS depends on the job's fate.
    if (job.status === 'running' || job.status === 'recovering' || job.status === 'retrying') {
      if (!activePlaced) {
        out.push({ key, label, state: 'active' });
        activePlaced = true;
      } else {
        out.push({ key, label, state: 'waiting' });
      }
    } else if (job.status === 'failed' || job.status === 'cancelled') {
      // The line stopped here: mark the first unreached step with the job's
      // fate, the rest as never-reached.
      if (!activePlaced) {
        out.push({ key, label, state: job.status === 'failed' ? 'failed' : 'stopped' });
        activePlaced = true;
      } else {
        out.push({ key, label, state: 'waiting' });
      }
    } else {
      out.push({ key, label, state: 'waiting' });
    }
  }
  return out;
}

const NODE: Record<PipelineStepState, { icon: typeof Check; className: string; ring: string; spin?: boolean }> = {
  done: { icon: Check, className: 'text-status-positive-text', ring: 'border-status-positive-text/50 bg-status-positive-text/10' },
  warn: { icon: AlertTriangle, className: 'text-status-attention-text', ring: 'border-status-attention-text/50 bg-status-attention-text/10' },
  failed: { icon: X, className: 'text-status-negative-text', ring: 'border-status-negative-text/50 bg-status-negative-text/10' },
  active: { icon: Loader2, className: 'text-brand', ring: 'border-brand/60 bg-brand/10', spin: true },
  waiting: { icon: Minus, className: 'text-text-tertiary/60', ring: 'border-sidebar-border' },
  stopped: { icon: Ban, className: 'text-text-tertiary', ring: 'border-sidebar-border' },
};

const STATE_WORD: Record<PipelineStepState, string> = {
  done: 'done',
  warn: 'needs review',
  failed: 'failed',
  active: 'in progress',
  waiting: 'waiting',
  stopped: 'stopped',
};

export function PreparePipeline({ steps }: { steps: PipelineStep[] }) {
  return (
    <ol
      className="flex items-start gap-0"
      aria-label={`Preparation steps: ${steps.map((s) => `${s.label} ${STATE_WORD[s.state]}`).join(', ')}`}
    >
      {steps.map((step, i) => {
        const meta = NODE[step.state];
        const Icon = meta.icon;
        return (
          <li
            key={step.key}
            className="flex min-w-0 flex-1 flex-col items-center gap-1"
            title={`${step.label}: ${STATE_WORD[step.state]}${step.detail ? ` (${step.detail})` : ''}`}
          >
            <div className="flex w-full items-center">
              <div
                className={cn(
                  'h-px flex-1',
                  i === 0 ? 'bg-transparent' : 'bg-sidebar-border',
                )}
                aria-hidden
              />
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                  meta.ring,
                )}
              >
                <Icon className={cn('h-3 w-3', meta.className, meta.spin ? 'animate-spin' : '')} aria-hidden />
              </span>
              <div
                className={cn(
                  'h-px flex-1',
                  i === steps.length - 1 ? 'bg-transparent' : 'bg-sidebar-border',
                )}
                aria-hidden
              />
            </div>
            <span
              className={cn(
                'max-w-full truncate text-[9px] leading-tight',
                step.state === 'active'
                  ? 'font-medium text-sidebar-foreground'
                  : step.state === 'waiting'
                    ? 'text-text-tertiary/70'
                    : 'text-text-tertiary',
              )}
            >
              {step.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
